import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { PdfViewerModalComponent } from '../../../../shared/components/pdf-viewer-modal/pdf-viewer-modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { ReportKeys } from '../../../reports/core/enums/ReportKeys';
import { ReportsService } from '../../../reports/core/services/ReportsService';
import {
  NonTakenReservationsToReleaseModalComponent
} from '../../../reservations/modals/non-taken-reservations-to-release-modal/non-taken-reservations-to-release-modal.component';
import {
  TerminalSessionTransactionsModalComponent
} from '../../../terminals/modals/terminal-session-transactions-modal/terminal-session-transactions-modal.component';
import {
  TicketDetailsModalComponent
} from '../../../tickets/modals/ticket-details-modal/ticket-details-modal.component';
import { TicketNumberModalComponent } from '../../../tickets/modals/ticket-number-modal/ticket-number-modal.component';
import { WorkerService } from '../../../worker/core/services/WorkerService';
import { finalize } from 'rxjs';
import { ToolsService } from '../../../../core/services/ToolsService';
import { CartStore } from '../../../cart/core/stores/CartStore';
import {
  ConfirmationModalComponent
} from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { CartService } from '../../../cart/core/services/CartService';
import { AppStore } from '../../../../core/stores/AppStore';
import { WebCashPermissionControlDirective } from '../../../auth/core/directives/web-cash-permission-control.directive';

@Component({
  selector: 'app-actions-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    NonTakenReservationsToReleaseModalComponent,
    TerminalSessionTransactionsModalComponent,
    TicketDetailsModalComponent,
    TicketNumberModalComponent,
    PdfViewerModalComponent,
    WebCashPermissionControlDirective,
  ],
  templateUrl: './actions-modal.component.html',
  styleUrl: './actions-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActionsModalComponent extends BaseModalComponent {

  @ViewChild('pdfViewerModalComponent') pdfViewerModalComponent: PdfViewerModalComponent;

  public readonly isClearCacheButtonDisabled = signal(false);

  private readonly _reportsService = inject(ReportsService);
  private readonly _workerService = inject(WorkerService);
  private readonly _toolsService = inject(ToolsService);
  private readonly _cartStore = inject(CartStore);
  private readonly _cartService = inject(CartService);

  public openSafe(): void {
    if (!this.permissionService.hasPermission(this.permissionKeys.WebCxCashSafes)) {
      return;
    }

    this._workerService.openSafe().subscribe({
      error: ({ error }: ApiErrorResponse) => {
        this.notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`open-safe/notifications/${error.message}.Message`)
        );
      }
    });
  }

  public printLastInvoice(): void {
    const terminalSessionId = this.terminalsStore.selectedTerminal()?.currentSession?.id;
    if (!terminalSessionId) {
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'No active terminal session found'.trans('notifications/no-active-terminal-session-found.Message')
      );
      return;
    }

    this._reportsService.generateReport(ReportKeys.LastTransactionInvoice, terminalSessionId)
      .subscribe({
        next: res =>
          this.pdfViewerModalComponent.open({ base64Content: res.data.base64Content }),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`generate-invoice/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public printLastReceipt(): void {
    const terminalSessionId = this.terminalsStore.selectedTerminal()?.currentSession?.id;
    if (!terminalSessionId) {
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'No active terminal session found'.trans('notifications/no-active-terminal-session-found.Message')
      );
      return;
    }

    this._workerService.printLastReceipt(terminalSessionId)
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`print-last-receipt/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public generateEmployeeSessionReport(): void {
    this._reportsService.generateReport(ReportKeys.EmployeeSession)
      .subscribe({
        next: res =>
          this.pdfViewerModalComponent.open({ base64Content: res.data.base64Content }),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`generate-employee-session-report/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public clearCache(): void {
    const fn = (): void => {
      this.isClearCacheButtonDisabled.set(true);

      this.notificationService.pushInfo(
        'Info'.trans('notifications/info.Title'),
        'Clear cache process started.'.trans(`actions-modal/notifications/clearCacheProcess.Message`)
      );

      this._toolsService.clearCashCache()
        .subscribe({
          next: () => {
            this.notificationService.pushSuccess(
              'Erfolg'.trans('notifications/success.Title'),
              'Cache cleared.'.trans(`actions-modal/notifications/cacheCleared.Message`)
            );

            setTimeout(() => {
              AppStore.shouldPreventAppReload.set(false);
              window.location.reload();
            }, 1000);
          },
          error: ({ error }: ApiErrorResponse) => {
            this.isClearCacheButtonDisabled.set(false);
            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`clear-cache/notifications/${error.message}.Message`)
            );
          }
        });
    };

    if (this._cartStore.cart()?.items.length > 0) {
      ConfirmationModalComponent.openConfirmationModal(
        'Clear cache'.trans('actions-modal/clear-cache-action/cart-has-items-alert-modal.Title'),
        'Cart must be empty to clear the cache. Do you want to empty your cart and clear cache?'.trans('actions-modal/clear-cache-action/cart-has-items-alert-modal.Text'),
        this.destroyRef,
        () => {
          this._cartService.clearCart()
            .subscribe({
              next: () => fn(),
              error: ({ error }: ApiErrorResponse) => {
                this.notificationService.pushError(
                  'Fehler'.trans('notifications/error.Title'),
                  error.message.trans(`clear-cart/notifications/${error.message}.Message`)
                );
              }
            });
        }
      );
    } else {
      fn();
    }
  }
}
